import {CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT, OK} from "../constants/response-status-codes";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { EmailAccountType } from "../models/Account";
import { EmailAccount, EmailAccountWrapper } from "../models/EmailAccount";
import { genericError} from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { buildAccountFields } from "../utilities/AccountUtils";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { Deserializer } from 'jsonapi-serializer';
import { EMAIL_ACCOUNT_CONSTANTS } from "../constants/account-constants";
import { getActiveStatus } from "./utilities/StatusBuilder";

const apiConnector = new APIConnector();

export const deleteEmailAccount = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `account/${id}`;
        const emailAccount = {
            data: {
                type: "account",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, emailAccount)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAllEmailAccounts = async (translate: any, listQuery?: ListQuery): Promise<EmailAccountWrapper> => {
    try {
        const filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const partialUrl = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filter)
        const url = `accountEmail?${partialUrl}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiEmailAccounts: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const emailAccounts: EmailAccount[] = apiEmailAccounts.map(apiEmailAccount => {
                    return {
                        id: apiEmailAccount['account-id'].toString(),
                        name: apiEmailAccount['acc-name'],
                        accActive: apiEmailAccount['acc-active'],
                        accDefault: apiEmailAccount['acc-default'] === 0 ? translate.byKey(TranslationKeys.no) : translate.byKey(TranslationKeys.yes),
                        authEnabled: apiEmailAccount['auth-enabled'] === 'true' ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                        outgoingServer: apiEmailAccount['outgoing-server'],
                        portNumber: apiEmailAccount['port-number'],
                        sslEnabled: apiEmailAccount['ssl-enabled'] === 'true' ? translate.byKey(TranslationKeys.enabled) : translate.byKey(TranslationKeys.disabled),
                        username: apiEmailAccount['username'],
                        isPasswordCaptured: apiEmailAccount['is-password-captured'],
                        status: getActiveStatus(apiEmailAccount['acc-active'] ? apiEmailAccount['acc-active'].toString() : "0")
                    }
                })

                return resolve({rows: emailAccounts, pagination: pagination});
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getEmailAccountNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const partialUrl = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const accountEmailFields = "fields[accountEmail]=accName,accountId";
        const url = `accountEmail?${partialUrl}&${accountEmailFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiEmailAccounts: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });;
                }

                const emailAccount: Name[] = apiEmailAccounts.map((apiEmailAccount) => {
                    return { label: apiEmailAccount['acc-name'], value: apiEmailAccount['account-id'].toString() };
                });

                resolve({ rows: emailAccount, pagination: newPagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeEmailAccount = async (emailAccount: EmailAccountType, createEmailAccount = true) :Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "account",
            attributes: {
                accName: emailAccount.name,
                accActive: emailAccount.enabled ? 1 : 0,
                accDefault: emailAccount.defaultAccount ? 1 : 0,
                tempPassword: emailAccount.password === undefined ? null : emailAccount.password,
            },
            relationships: {
                accountTypeId: 2,
                accountValues: [
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.OUTGOING_SERVER,
                        avData: emailAccount.outgoingServer
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.OUTGOING_PORT,
                        avData: emailAccount.outgoingPort
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.SSL,
                        avData: emailAccount.ssl
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.AUTH,
                        avData: emailAccount.auth
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.INCOMING_SERVER,
                        avData: emailAccount.incomingServer
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.INCOMING_PORT,
                        avData: emailAccount.incomingPort
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.ADDRESS,
                        avData: emailAccount.address
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.USERNAME,
                        avData: emailAccount.username
                    },
                    {
                        accountAttributeLookupName: EMAIL_ACCOUNT_CONSTANTS.PASSWORD,
                        avData: emailAccount.password
                    }
                ]
            }
        }

        const url = "";
        const emailAccountResponse =   await apiConnector.sendData(url, true, newRecord, true)

        return new Promise(async (resolve, reject) => {
            if(emailAccountResponse){
                if(emailAccountResponse.status === OK || emailAccountResponse.status ===CREATED){
                    try{
                        const emailAccountJson = await emailAccountResponse.json()
                        return resolve({id:emailAccountJson['attributes']?.['id'],success:true})
                    }catch (exception){
                        return reject({status:emailAccountResponse.status})
                    }
                }else {
                    if(emailAccountResponse.body){
                        if(emailAccountResponse.statusText){
                            const content = await emailAccountResponse.json()
                            return  reject({status:emailAccountResponse.status,success: false, message: content.errors.map(error => getConstraintViolation(error.detail))})
                        }else {
                            return reject({ status: emailAccountResponse.status,  message: genericError, success: false });
                        }
                    }else {
                        return reject({ status: emailAccountResponse.status, message: genericError, success: false });
                    }
                }
            }else {
                return reject({ status: INTERNAL_SERVER_ERROR, message: genericError, success: false });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getEmailAccountById = async (id: string, listParameters?: ListQuery): Promise<EmailAccountType> => {
    try {
        const accountValueFields = 'fields[accountValue]=account,accountAttribute,avData';
        const accountFields = 'fields[account]=accName,accActive,accDefault,isPasswordCaptured';
        const accountAttributes = 'fields[accountAttribute]=aaName';
        const url = `accountValue?filter=account.id==${id}&include=accountAttribute,account&${accountValueFields}&${accountFields}&${accountAttributes}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    reject({ status: response.status, message: genericError });
                }

                const emailAccount: EmailAccountType =
                    buildAccountFields(json);

                return resolve(emailAccount);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const editAccountValue = async (id: string, value: string) => {
    try {
        const url = `accountValue/${id}`;
        let response: Response;
        const updatedRecord = {
            data: {
                type: "accountValue",
                id: id,
                attributes: {
                    avData: value,
                },
                relationships: {},
            },
        };

        response = await apiConnector.sendData(url, false, updatedRecord);

        return response;
    } catch (error) {
        return new Promise(async (resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const editEmailAccount = async (id:string, value:EmailAccountType) => {
    try {
        const url = `account/${id}`;

        const updatedRecord = {
            "data": {
                "type": "account",
                "id": id,
                "attributes": {
                    ...(value.name && {accName: value.name}),
                    ...(value.defaultAccount !==undefined && {accDefault: value.defaultAccount ? 1: 0}),
                    ...(value.enabled !==undefined && {accActive: value.enabled ? 1: 0}),
                    ...((value.password !==undefined && value.password.label !== undefined) && {tempPassword: value.password.label}),
                },
                "relationships": {

                }
            }
        }
        // Sending data and waiting for response
        const response = await apiConnector.sendData(url, false, updatedRecord);

        // Checking if the response status is a fail and throwing an error if it is
        if (response.status !== NO_CONTENT) {
            const json = await response.json();
            return new Promise((resolve, reject) => {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            })
        }

        return response
    } catch (error) {
        return new Promise(async (resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
